import { useEffect, useRef } from 'react';

/**
 * Logs the user out after `timeoutMs` of true inactivity (no mouse, keyboard,
 * touch, or scroll events). Activity in any browser tab on the same origin
 * also resets the timer (via localStorage 'storage' events) so users with two
 * dashboard tabs open don't get logged out from the inactive tab.
 *
 * Usage (inside an authenticated layout):
 *   useIdleLogout({ timeoutMs: 15 * 60 * 1000, onTimeout: handleLogout });
 */
export default function useIdleLogout({ timeoutMs = 15 * 60 * 1000, onTimeout, enabled = true }) {
  const timerRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);

  // Keep latest onTimeout without restarting the effect.
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  useEffect(() => {
    if (!enabled) return undefined;

    const STORAGE_KEY = 'nextbell:lastActivity';

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        try { onTimeoutRef.current && onTimeoutRef.current(); } catch { /* noop */ }
      }, timeoutMs);
    };

    const markActivity = () => {
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore quota errors */ }
      reset();
    };

    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) reset();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'visibilitychange'];
    events.forEach(ev => window.addEventListener(ev, markActivity, { passive: true }));
    window.addEventListener('storage', onStorage);

    reset();

    return () => {
      events.forEach(ev => window.removeEventListener(ev, markActivity));
      window.removeEventListener('storage', onStorage);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timeoutMs, enabled]);
}
